import React, { createContext, useContext, useState, useEffect } from "react";
import { sampleContacts, sampleProjects } from "../seedData";

const DataContext = createContext();

export const useDataMode = () => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error("useDataMode must be used within a DataProvider");
  }
  return context;
};

export const DataProvider = ({ children }) => {
  const [useCloudDatabase, setUseCloudDatabase] = useState(() => {
    // Check localStorage for saved preference
    const saved = localStorage.getItem("useCloudDatabase");
    return saved === "true";
  });

  const [localContacts, setLocalContacts] = useState(() => {
    // Load local contacts from localStorage or use seed data
    const saved = localStorage.getItem("localContacts");
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        console.error("Error parsing saved contacts:", e);
      }
    }
    // Generate IDs for seed data
    return sampleContacts.map((contact, index) => ({
      ...contact,
      id: `local-${index + 1}`,
    }));
  });

  const [localProjects, setLocalProjects] = useState(() => {
    // Load local projects from localStorage or use seed data
    const saved = localStorage.getItem("localProjects");
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        console.error("Error parsing saved projects:", e);
      }
    }
    // Generate IDs for seed data
    return sampleProjects.map((project, index) => ({
      ...project,
      id: `project-${index + 1}`,
    }));
  });

  // Save preferences to localStorage
  useEffect(() => {
    localStorage.setItem("useCloudDatabase", useCloudDatabase.toString());
  }, [useCloudDatabase]);

  // Save local contacts to localStorage
  useEffect(() => {
    if (!useCloudDatabase) {
      localStorage.setItem("localContacts", JSON.stringify(localContacts));
    }
  }, [localContacts, useCloudDatabase]);

  // Save local projects to localStorage
  useEffect(() => {
    if (!useCloudDatabase) {
      localStorage.setItem("localProjects", JSON.stringify(localProjects));
    }
  }, [localProjects, useCloudDatabase]);

  const toggleDataMode = () => {
    setUseCloudDatabase((prev) => !prev);
  };

  const resetLocalData = () => {
    const freshContacts = sampleContacts.map((contact, index) => ({
      ...contact,
      id: `local-${index + 1}`,
    }));
    const freshProjects = sampleProjects.map((project, index) => ({
      ...project,
      id: `project-${index + 1}`,
    }));
    setLocalContacts(freshContacts);
    setLocalProjects(freshProjects);
    localStorage.setItem("localContacts", JSON.stringify(freshContacts));
    localStorage.setItem("localProjects", JSON.stringify(freshProjects));
  };

  const value = {
    useCloudDatabase,
    toggleDataMode,
    localContacts,
    setLocalContacts,
    localProjects,
    setLocalProjects,
    resetLocalData,
  };

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
};
